import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {UserAssetSelector} from '../../Selectors';
import useStatusChecker from './useStatusChecker';
import {AIMode, AssetPart, AssetStatus} from '@taopix/taopix-core';
import {useTaopix} from '../../Components/Context/TaopixContext';
import {COLOUR_COMPONENT_FLATTENED} from '../../Constants';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {uploadStats, AssetWorkflow} from '@taopix/taopix-asset-manager';
import {useExperience} from '../../Components/Experience/ExperienceProvider';

const useQueuedAssetObserver = () => {
  const app = useTaopix();
  const monitor = useStatusChecker();
  const assets = useSelector(UserAssetSelector);
  const {getAIMode} = useExperience();

  const [manifest, setManifest] = useState<number[]>([]);

  useEffect(() => {
    // add any uploaded (but not processed images to the monitor)
    setManifest(current => {
      return Array.from(
        new Set([
          ...current,
          ...assets
            .filter(
              a =>
                AssetStatus.Uploaded === a.status &&
                !Object.prototype.hasOwnProperty.call(a, 'uploadId') // omit any assets which are current;y being processed for the first time
            )
            .map(a => a.id),
        ])
      );
    });
  }, [assets]);

  useEffect(() => {
    if (!monitor) {
      return;
    }

    monitor.on('update', onUpdate);

    manifest.forEach(id => {
      if (!monitor.includes(id)) {
        monitor.add(id);
      }
    });

    return () => {
      monitor.off('update', onUpdate);
    };
  }, [monitor, manifest]);

  const onUpdate = event => {
    const updates = event.detail;

    // update each of the assets
    updates.forEach(update => {
      // if this is an AI update, ignore
      if (update?.metaData) {
        return;
      }

      // we only want to check assets in the manifest
      if (!manifest.includes(update.id)) {
        return;
      }

      // the page state manager gives us an easy way of fetching an asset from the store
      const asset = app.core.get('stateManager').getAsset(update.id as number);
      if (!asset) {
        return;
      }

      // if the asset is already processed, ignore
      if (AssetStatus.Processed === asset.status) {
        return;
      }

      // get a flattened FPO to use for the thumbnail
      const thumbsrc =
        update?.children?.find(
          c =>
            c &&
            c?.part === AssetPart.Intermediate &&
            c?.colourComponent === COLOUR_COMPONENT_FLATTENED
        )?.src || '';

      if ('' !== asset.blobUrl) {
        // eslint-disable-next-line node/no-unsupported-features/node-builtins
        URL.revokeObjectURL(asset.blobUrl);
      }

      // update the asset
      app.core.get('stateManager').updateAsset({
        ...asset,
        ...(update as unknown as AssetMetaInterface),
        src: thumbsrc,
        thumbnailsrc: thumbsrc,
        hiressrc: thumbsrc,
        blobUrl: '',
      });

      // update the stats
      uploadStats.up(AssetWorkflow.DEFAULT, 'PROCESSED');
      uploadStats.up(
        AssetWorkflow.DEFAULT,
        'CURRENTDATAUPLOADED',
        Math.round((asset.fileSizeBytes / 1024) * 100) / 100
      );

      if (getAIMode() > AIMode.NotAvailable) {
        uploadStats.up(AssetWorkflow.AI, 'READY');
        uploadStats.down(AssetWorkflow.AI, 'UNPROCESSED');
        uploadStats.up(AssetWorkflow.AI, 'PROCESSED');
      }

      setManifest(current => {
        return current.filter(id => id !== update.id);
      });
    });
  };
};

export default useQueuedAssetObserver;
